import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  berlinDate,
  diffDays,
  type FinanceDashboard,
  type ChatOverview,
  type FleetOverview,
  type Inquiry,
  type OfferSummary,
  type PayrollOverview,
  type Paginated,
  type PlanningBoard,
  type TaskSummary,
} from '@kontor/shared';
import { api, queryString } from './api';
import { useSession } from './session';

const POLL = 5 * 60_000;
/** Chats und neue Anfragen warten auf eine schnelle Antwort: kurzer Takt. Die Abfragen sind kleine Zählungen, und TanStack Query pausiert sie, solange der Tab im Hintergrund liegt. */
const POLL_FAST = 20_000;
const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export interface Notice {
  key: string;
  /** alert: braucht Aufmerksamkeit (rot), info: gut zu wissen */
  tone: 'alert' | 'info';
  text: string;
  to: string;
  /** Ändert sich, wenn derselbe Hinweis neu auftritt, obwohl der Text gleich bleibt (neuer Chat: „1 Live Chat wartet“). Ein als gelesen markierter Hinweis erscheint dann wieder als ungelesen. */
  stamp?: string;
}

/** Abfrage für ein Widget. Der Schlüssel beginnt mit „dashboard“, damit er nie mit der Liste im Modul zusammenfällt, die andere Felder braucht. */
export function useWidget<T>(key: unknown[], path: string, enabled: boolean, fast = false): UseQueryResult<T> {
  return useQuery({ queryKey: ['dashboard', ...key], queryFn: () => api<T>('GET', path), enabled, staleTime: fast ? POLL_FAST / 2 : 60_000, refetchInterval: fast ? POLL_FAST : POLL, refetchOnWindowFocus: true });
}

/**
 * Hinweise, die jetzt Aufmerksamkeit brauchen: überfällige Aufgaben, abgelaufene und bald ablaufende Angebote, überfällige Rechnungen, neue Anfragen, fällige Wiedervorlagen,
 * Aufträge, die auf Planung warten, und überfällige Fristen der Flotte. Nur was die Rolle in lizenzierten Modulen sehen darf. Die Übersicht und die Glocke in der Kopfleiste
 * zeigen dieselbe Liste, die Abfragen teilen sich den Zwischenspeicher.
 */
export function useNotices(): { notices: Notice[]; loading: boolean } {
  const { session, can, hasModule } = useSession();
  const me = session?.user.id;
  const useInquiries = hasModule('inquiries') && can('inquiries.read');
  const useOffers = hasModule('offers') && can('offers.read');
  const useCalendar = hasModule('calendar') && can('calendar.read');
  const useFinance = hasModule('invoices_out') && can('invoices_out.read') && can('invoices_out.read_amounts');
  const useCrm = hasModule('crm') && can('crm.read');
  const usePlanning = hasModule('orders') && can('orders.plan');
  const useFleet = hasModule('fleet') && can('fleet.read');
  const useChat = hasModule('webchat') && can('webchat.read');
  const usePayroll = hasModule('payroll') && can('payroll.read');

  const inquiries = useWidget<Paginated<Inquiry>>(['inquiries'], `/inquiries${queryString({ status: 'new', perPage: 5 })}`, useInquiries, true);
  const offers = useWidget<Paginated<OfferSummary>>(['offers'], `/offers${queryString({ status: 'sent', perPage: 50 })}`, useOffers);
  const summary = useWidget<{ data: TaskSummary }>(['task-summary', me], `/tasks/summary${queryString({ involvedUserId: me })}`, useCalendar && !!me);
  const finance = useWidget<{ data: FinanceDashboard }>(['finance'], '/invoices-out/dashboard', useFinance);
  const followUps = useWidget<Paginated<unknown>>(['follow-ups-due'], `/crm/follow-ups${queryString({ scope: 'due', perPage: 1 })}`, useCrm);
  const planning = useWidget<{ data: PlanningBoard }>(['planning'], '/orders/planning', usePlanning);
  const fleet = useWidget<{ data: FleetOverview }>(['fleet-overview'], '/fleet/overview', useFleet);
  const chats = useWidget<{ data: ChatOverview }>(['webchat-overview'], '/webchat/overview', useChat, true);
  const payroll = useWidget<{ data: PayrollOverview }>(['payroll'], '/payroll/overview', usePayroll);

  const today = berlinDate();
  const notices: Notice[] = [];
  const overdue = summary.data?.data.overdue ?? 0;
  const expiring = (offers.data?.data ?? []).filter((o) => o.validUntil && diffDays(today, o.validUntil) >= 0 && diffDays(today, o.validUntil) <= 7);
  const lapsed = (offers.data?.data ?? []).filter((o) => o.validUntil && diffDays(today, o.validUntil) < 0);
  const newCount = inquiries.data?.meta.total ?? 0;
  const fin = finance.data?.data;
  const due = followUps.data?.meta.total ?? 0;
  const waiting = planning.data?.data.queue.length ?? 0;
  const chatsWaiting = chats.data?.data.waiting ?? 0;
  const lateDeadlines = fleet.data?.data.deadlines.filter((d) => d.state === 'overdue').length ?? 0;

  if (overdue > 0) notices.push({ key: 'overdue', tone: 'alert', text: `${plural(overdue, 'Aufgabe ist', 'Aufgaben sind')} überfällig.`, to: '/kalender/aufgaben?group=overdue&mine=1' });
  if (chatsWaiting > 0) notices.push({ key: 'chats', tone: 'alert', text: `${plural(chatsWaiting, 'Live Chat wartet', 'Live Chats warten')} auf eine Antwort.`, to: '/chats', stamp: chats.data?.data.latestWaitingAt ?? undefined });
  if (due > 0) notices.push({ key: 'follow-ups', tone: 'alert', text: `${plural(due, 'Wiedervorlage ist', 'Wiedervorlagen sind')} fällig.`, to: '/crm' });
  if (lapsed.length > 0) notices.push({ key: 'lapsed', tone: 'alert', text: `${plural(lapsed.length, 'Angebot ist', 'Angebote sind')} abgelaufen, ohne dass der Kunde geantwortet hat.`, to: '/angebote?status=sent' });
  if (fin && fin.overdueCount > 0) notices.push({ key: 'invoices', tone: 'alert', text: `${plural(fin.overdueCount, 'Rechnung ist', 'Rechnungen sind')} überfällig, zusammen ${eur.format(fin.overdueCents / 100)}.`, to: '/rechnungen?payment=overdue' });
  if (lateDeadlines > 0) notices.push({ key: 'fleet', tone: 'alert', text: `${plural(lateDeadlines, 'Frist der Flotte ist', 'Fristen der Flotte sind')} überfällig (HU, Versicherung oder Wartung).`, to: '/flotte' });
  if (waiting > 0) notices.push({ key: 'planning', tone: 'info', text: `${plural(waiting, 'Auftrag wartet', 'Aufträge warten')} auf die Planung.`, to: '/auftragsplanung' });
  if (expiring.length > 0) notices.push({ key: 'expiring', tone: 'info', text: `${plural(expiring.length, 'Angebot läuft', 'Angebote laufen')} in den nächsten 7 Tagen ab.`, to: '/angebote?status=sent' });
  if (newCount > 0) notices.push({ key: 'new', tone: 'info', text: `${plural(newCount, 'neue Anfrage wartet', 'neue Anfragen warten')} auf Bearbeitung.`, to: '/anfragen?status=new' });

  for (const r of payroll.data?.data.reminders ?? []) notices.push({ key: r.key, tone: r.tone, text: r.text, to: r.to, stamp: r.stamp });

  const loading = [inquiries, offers, summary, finance, followUps, planning, fleet, chats, payroll].some((q) => q.isLoading && q.fetchStatus !== 'idle');
  return { notices, loading };
}
